import { useId, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronRight, FileText, Flag, List, Search } from 'lucide-react'
import type { ActivityKind, ActivityStep } from '@/api/types'
import { riseIn, spring, stagger } from '@/lib/motion'

const icons: Record<ActivityKind, typeof Search> = {
  search: Search,
  read: FileText,
  list: List,
  flag: Flag,
}

function summarize(steps: ActivityStep[]): string {
  const searches = steps.filter((s) => s.kind === 'search').length
  const reads = steps.filter((s) => s.kind === 'read').length
  const parts: string[] = []
  if (searches) parts.push(`${searches} ${searches === 1 ? 'search' : 'searches'}`)
  if (reads) parts.push(`${reads} ${reads === 1 ? 'document' : 'documents'} read`)
  return parts.join(' · ') || `${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`
}

/** Collapsible "Steps" disclosure: open while streaming, collapsed once done (unless toggled). */
export function ActivitySteps({ steps, streaming }: { steps: ActivityStep[]; streaming: boolean }) {
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const open = userOpen ?? streaming
  const listId = useId()

  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setUserOpen(!open)}
        className="pressable -ml-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium text-ink-faint hover:bg-subtle hover:text-ink"
      >
        <motion.span animate={{ rotate: open ? 90 : 0 }} transition={spring.snappy} className="inline-flex">
          <ChevronRight className="size-3.5" aria-hidden />
        </motion.span>
        Steps
        <span className="font-normal">· {summarize(steps)}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="steps"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={spring.smooth}
            className="overflow-hidden"
          >
            <motion.ol
              id={listId}
              variants={stagger(0.04)}
              initial="hidden"
              animate="show"
              className="relative ml-[7px] mt-2.5 space-y-2.5 pl-4 before:absolute before:inset-y-1 before:left-0 before:w-px before:bg-gradient-to-b before:from-line before:to-transparent"
            >
              {steps.map((step, index) => {
                const Icon = icons[step.kind] ?? Search
                return (
                  <motion.li key={index} variants={riseIn} className="flex gap-2.5 text-xs leading-5 text-ink-muted">
                    <span className="mt-px flex size-[18px] shrink-0 items-center justify-center rounded-full bg-subtle text-ink-faint">
                      <Icon className="size-3" aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <span>{step.label}</span>
                      {typeof step.results === 'number' && (
                        <span className="text-ink-faint"> · {step.results} results</span>
                      )}
                      {step.queries && step.queries.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {step.queries.map((query) => (
                            <span
                              key={query}
                              className="rounded-full bg-surface px-2 py-0.5 text-[11px] text-ink-muted shadow-card"
                            >
                              {query}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.li>
                )
              })}
            </motion.ol>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
